import { useContext } from 'react';
import styled, { css } from 'styled-components';
import Col from '~/grid/Col';
import Image from '../../molecules/Image';
import { mq } from '~/styles/grid';
import Video from '../../atoms/Video';
import HeaderHeightContext from '~/helpers/HeaderHeightContext';
import { ContentHubTeaserCard } from '../../molecules/ContentHubTeaserCard';
import useIsMobile from '~/hooks/useIsMobile';

const HeroContentHubWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  width: calc(100% + 25px);
  position: relative;
  max-width: 1140px;
  transition: margin 0.5s ease-out;
  padding-top: 40px;
  overflow: hidden;
  min-height: ${(props) => 420 + props.$headerHeight + 40}px;

  video {
    width: 100%;
  }

  audio {
    ${(props) =>
      !props.theme.header.mobileSwitch &&
      css`
        margin-top: 45px;
      `};
    margin-bottom: -10px;
    width: 100%;
  }

  @media (${(props) => props.theme.header.mobileSwitch}) {
    margin: 0;
    padding-top: 15px;
  }

  @media (${mq.sm}) {
    min-height: ${(props) => props.theme.hero.bgImageHeight.sm};
  }

  @media (${mq.xs}) {
    min-height: ${(props) => props.theme.hero.bgImageHeight.xs};
  }
`;

const HeroContentHubDescription = styled.div`
  color: ${(props) => props.theme.color.primary800};
  margin-top: 12.5px;
`;

/**
 * @param {HeroContentHubProps} props
 * @returns {React.ReactElement} The rendered component
 */
export const HeroContentHub = ({
  image,
  title,
  description,
  mediaType,
  media,
  postType,
  topic,
  topicPageUrl,
  date,
  url,
  id,
  isContentHubCategoryPage,
}) => {
  const headerHeight = useContext(HeaderHeightContext);
  const isMobile = useIsMobile({ useMq: 'mdDown' });

  const cols = [
    { colAmount: { xl: 3.5, lg: 3.5, md: 3.5, sm: 12, xs: 12 } },
    { colAmount: { xl: 8, lg: 8, md: 8, sm: 12, xs: 12 } },
  ];

  const { formats, poster, videoId, cloudName, provider, hash, type } =
    media || {};
  const { ogg, mp4, webm, mp3 } = formats || {};

  return (
    <HeroContentHubWrapper $headerHeight={headerHeight} heroEVP>
      {(!isMobile || mediaType === 'TEXT') && (
        <Col colAmount={cols[0].colAmount}>
          <ContentHubTeaserCard
            inHero
            withPicture={false}
            date={date}
            id={id || ''}
            title={title}
            postType={postType}
            mediaType={mediaType}
            topic={topic}
            topicPageUrl={topicPageUrl}
            url={url}
          />
        </Col>
      )}
      <Col colAmount={cols[1].colAmount}>
        {mediaType === 'TEXT' && (!isContentHubCategoryPage || image?.url) && (
          <>
            <Image url={image?.url} fullHeight />
          </>
        )}
        {mediaType === 'VIDEO' && formats && (
          <video controls poster={poster || image?.url}>
            {webm && <source src={webm} type="video/webm" />}
            {ogg && <source src={ogg} type="video/ogg" />}
            {mp4 && <source src={mp4} type="video/mp4" />}
          </video>
        )}
        {mediaType === 'VIDEO' && videoId && provider && (
          <Video
            videoId={videoId}
            cloudName={cloudName}
            poster={poster || image?.url}
            provider={provider}
            hash={hash}
            type={type}
          />
        )}
        {mediaType === 'AUDIO' && !isMobile && <Image url={image?.url} />}
        {mediaType === 'AUDIO' && formats && (
          <audio controls>{mp3 && <source src={mp3} type="audio/mp3" />}</audio>
        )}
        {description && (
          <HeroContentHubDescription>{description}</HeroContentHubDescription>
        )}
      </Col>
    </HeroContentHubWrapper>
  );
};
